import { useState, type FormEvent } from 'react';
import {
  MAX_FEATURE_REQUEST_MESSAGE_LENGTH,
  featureRequestStatusLabel,
  type FeatureRequestStatus,
} from '@srcbattle/shared';
import type { FeatureRequest } from '../api.ts';
import { Button } from './ui.tsx';

/**
 * The status badge.
 *
 * Four states, four tones, using the tokens the rest of the app already
 * defines rather than fresh colours: waiting is quiet, accepted and done are
 * the app's success colour, declined is its danger colour. A badge that
 * introduces its own palette is how a small feature starts to look bolted on.
 */
export function StatusBadge({ status }: { status: FeatureRequestStatus }) {
  const tones: Record<FeatureRequestStatus, string> = {
    open: 'border-border-strong bg-surface-raised text-text-muted',
    accepted: 'border-accent/40 bg-accent/15 text-accent',
    declined: 'border-danger/40 bg-danger/10 text-danger',
    completed: 'border-success/40 bg-success/10 text-success',
  };
  return (
    <span className={`shrink-0 rounded border px-1.5 py-0.5 text-xs ${tones[status]}`}>
      {featureRequestStatusLabel(status)}
    </span>
  );
}

/** A dot rather than a number: one unread thread and nine read the same way. */
export function UnreadDot({ count, label }: { count: number; label: string }) {
  if (count < 1) return null;
  return (
    <span
      aria-label={label}
      className="ml-1 inline-flex min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-4 text-accent-fg"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

function when(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The conversation on one request, and the box to add to it.
 *
 * Shared by the member's page and the admin panel so both sides read the same
 * thread in the same order — including the status changes, which sit inline
 * rather than in a separate log. "Why was this declined" should be answerable
 * by reading downwards.
 */
export function FeatureRequestThread({
  request,
  busy,
  onReply,
}: {
  request: FeatureRequest;
  busy: boolean;
  onReply: (body: string) => void;
}) {
  const [draft, setDraft] = useState('');

  function send(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    onReply(text);
    setDraft('');
  }

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <p className="whitespace-pre-wrap text-sm text-text-muted">{request.body}</p>

      {request.messages && request.messages.length > 0 && (
        <ul className="space-y-2">
          {request.messages.map((message) => (
            <li
              key={message.id}
              className={`rounded-lg border p-2 text-sm ${
                message.kind === 'status'
                  ? 'border-dashed border-border-strong bg-transparent text-text-muted'
                  : message.fromAuthor
                    ? 'border-border bg-surface-raised'
                    : 'border-accent/30 bg-accent/10'
              }`}
            >
              <p className="mb-0.5 text-xs text-text-faint">
                {message.kind === 'status' ? 'Status' : message.authorName} · {when(message.createdAt)}
              </p>
              <p className="whitespace-pre-wrap">{message.body}</p>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={send} className="flex flex-col gap-2">
        <label className="text-xs font-medium text-text-muted" htmlFor={`reply-${request.id}`}>
          Add a message
        </label>
        <textarea
          id={`reply-${request.id}`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={2}
          maxLength={MAX_FEATURE_REQUEST_MESSAGE_LENGTH}
          placeholder="Anything else worth knowing?"
          className="w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-text placeholder:text-text-faint focus:outline-none focus-visible:outline-2 focus-visible:outline-accent"
        />
        <div className="flex justify-end">
          <Button type="submit" variant="secondary" loading={busy} disabled={!draft.trim()}>
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}
